'use client'

import { useEffect, useRef } from 'react'
import { parseISO } from 'date-fns'
import type { FormStateReturn, UnifiedDraftValues } from './useFormState'
import { DEFAULT_VALUES } from './useFormState'
import { createChecklistItems } from '@/entries'
import { findMatchingPeople } from '../ingest/fuzzy-match'
import type { CaptureEntryType } from '../types'
import type { AnyAnalysis } from '../analysis/useEntryAnalysis'
import type { Database } from '@/account'

interface UseAnalysisApplicationOptions {
  formState: FormStateReturn
  analysis: AnyAnalysis | null
  aiDetectedType: string | null
  effectiveType: CaptureEntryType | null
  isMediaDetected: boolean
  peopleForAnalysis: Array<{ id: string; name: string }>
  availableTopics: Array<{ id: string; name: string }>
  availableProjects: Array<{ id: string; name: string }>
  /** When set, project is always overridden to this value after analysis */
  enforcedProjectId?: string
}

/**
 * Applies AI analysis results to form state.
 * Handles title, people matching, topic matching, and task-specific fields.
 * Also resets form when content is cleared.
 */
export function useAnalysisApplication({
  formState,
  analysis,
  aiDetectedType,
  effectiveType,
  isMediaDetected,
  peopleForAnalysis,
  availableTopics,
  availableProjects,
  enforcedProjectId,
}: UseAnalysisApplicationOptions): void {
  // Apply analysis results
  useEffect(() => {
    if (!analysis || !aiDetectedType) return

    if (effectiveType) formState.setHasShownTitle(true)

    const analysisKey = `${aiDetectedType}:${JSON.stringify(analysis)}`
    if (formState.lastAppliedAnalysisRef.current === analysisKey) return
    formState.setLastAppliedAnalysis(analysisKey)

    const analysisObj = analysis as unknown as Record<string, unknown>

    const updates: Partial<UnifiedDraftValues> = {
      ...(formState.userModifiedRef.current.title ? {} : { title: '' }),
      ...(formState.userModifiedRef.current.people
        ? {}
        : { selectedPeopleIds: [] }),
      ...(formState.userModifiedRef.current.topics
        ? {}
        : { selectedTopicIds: [] }),
      ...(formState.userModifiedRef.current.project
        ? {}
        : { selectedProjectId: null }),
      ...(formState.userModifiedRef.current.category
        ? {}
        : { category: 'personal' }),
      ...(formState.userModifiedRef.current.priority
        ? {}
        : { priority: 'medium' }),
      ...(formState.userModifiedRef.current.important
        ? {}
        : { important: false }),
      ...(formState.userModifiedRef.current.dueDate ? {} : { dueDate: null }),
      ...(formState.userModifiedRef.current.checklist
        ? {}
        : { checklistItems: null }),
    }

    formState.setSuggestedPeople([])

    // Title
    if (
      !formState.userModifiedRef.current.title &&
      typeof analysisObj.title === 'string' &&
      analysisObj.title.trim()
    ) {
      updates.title = analysisObj.title
    }

    // People matching
    if (!formState.userModifiedRef.current.people) {
      const knownNames = (analysisObj.knownPeople ?? analysisObj.people) as
        | string[]
        | undefined
      if (Array.isArray(knownNames) && knownNames.length > 0) {
        updates.selectedPeopleIds = findMatchingPeople(
          knownNames,
          peopleForAnalysis,
        )
      }
    }

    // Suggested people
    if (Array.isArray(analysisObj.suggestedPeople)) {
      formState.setSuggestedPeople(analysisObj.suggestedPeople as string[])
    }

    // Topic matching
    if (!formState.userModifiedRef.current.topics) {
      const suggestedTopicNames = analysisObj.suggestedTopics as
        | string[]
        | undefined
      if (
        Array.isArray(suggestedTopicNames) &&
        suggestedTopicNames.length > 0
      ) {
        const matchedTopicIds = availableTopics
          .filter((topic) =>
            suggestedTopicNames.some(
              (name) => name.toLowerCase() === topic.name.toLowerCase(),
            ),
          )
          .map((topic) => topic.id)

        if (matchedTopicIds.length > 0) {
          updates.selectedTopicIds = matchedTopicIds
        }
      }
    }

    // Project matching
    if (!formState.userModifiedRef.current.project) {
      const suggestedProject = analysisObj.suggestedProject as
        | string
        | null
        | undefined
      if (typeof suggestedProject === 'string' && suggestedProject) {
        const matchedProject = availableProjects.find(
          (p) => p.name.toLowerCase() === suggestedProject.toLowerCase(),
        )
        if (matchedProject) {
          updates.selectedProjectId = matchedProject.id
        }
      }
    }

    // Task fields
    if (aiDetectedType === 'task') {
      if (
        !formState.userModifiedRef.current.category &&
        typeof analysisObj.category === 'string'
      ) {
        updates.category =
          analysisObj.category as Database['public']['Enums']['project_category']
      }
      if (
        !formState.userModifiedRef.current.priority &&
        typeof analysisObj.priority === 'string'
      ) {
        updates.priority =
          analysisObj.priority as Database['public']['Enums']['task_priority']
      }
      // Map AI priority to important boolean
      if (!formState.userModifiedRef.current.important) {
        const priority =
          typeof analysisObj.priority === 'string'
            ? analysisObj.priority
            : 'medium'
        updates.important = priority === 'urgent' || priority === 'high'
      }
      if (
        !formState.userModifiedRef.current.dueDate &&
        typeof analysisObj.dueDate === 'string'
      ) {
        try {
          const parsedDate = parseISO(analysisObj.dueDate)
          if (!isNaN(parsedDate.getTime())) {
            updates.dueDate = parsedDate
          }
        } catch {
          // Skip invalid dates
        }
      }
      if (
        !formState.userModifiedRef.current.checklist &&
        Array.isArray(analysisObj.checklistItems)
      ) {
        const items = (analysisObj.checklistItems as string[]).filter((s) =>
          s.trim(),
        )
        if (items.length > 0) {
          updates.checklistItems = createChecklistItems(items)
          // Clear description after extracting checklist (it's now redundant)
          updates.content = ''
        }
      }
    }

    // Override project when enforced (after AI enrichment runs normally)
    if (enforcedProjectId) {
      updates.selectedProjectId = enforcedProjectId
    }

    if (Object.keys(updates).length > 0) {
      Object.entries(updates).forEach(([key, value]) => {
        formState.setDraftValue(
          key as keyof UnifiedDraftValues,
          value as UnifiedDraftValues[keyof UnifiedDraftValues],
        )
      })

      const updatedFieldNames = Object.keys(updates).filter(
        (key) =>
          updates[key as keyof typeof updates] !==
          DEFAULT_VALUES[key as keyof typeof DEFAULT_VALUES],
      )
      if (updatedFieldNames.length > 0) {
        formState.setAiUpdatedFields(new Set(updatedFieldNames))
        setTimeout(() => formState.setAiUpdatedFields(new Set()), 500)
      }
    }
  }, [
    analysis,
    aiDetectedType,
    peopleForAnalysis,
    effectiveType,
    formState,
    availableTopics,
    availableProjects,
    enforcedProjectId,
  ])

  // Reset when content is cleared
  const prevContentRef = useRef(formState.content)
  useEffect(() => {
    const prevContent = prevContentRef.current
    prevContentRef.current = formState.content

    if (
      prevContent.trim().length > 0 &&
      formState.content.trim().length === 0
    ) {
      formState.setLastAppliedAnalysis(null)
      formState.resetDraft()
      formState.setUserSelectedType(null)
      formState.setSuggestedPeople([])
      formState.resetUserModified()
      // Restore enforced project after reset
      if (enforcedProjectId) {
        formState.setDraftValue('selectedProjectId', enforcedProjectId)
      }
    }
  }, [formState, enforcedProjectId])
}
